interface JobDataPoint {
  month: string;
  completed: number;
  pending: number;
  delivered: number;
}

interface JobCompletionChartProps {
  data: JobDataPoint[];
}

export default function JobCompletionChart({ data }: JobCompletionChartProps) {
  const maxVal = Math.max(...data.map((d) => d.completed + d.pending + d.delivered)) + 1;
  const chartH = 160;
  const chartW = 560;
  const padL = 32;
  const padR = 16;
  const padT = 16;
  const padB = 32;
  const innerW = chartW - padL - padR;
  const innerH = chartH - padT - padB;
  const n = data.length;
  const barGroupW = innerW / n;
  const barW = barGroupW * 0.55;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    val: Math.round(t * maxVal),
    y: padT + innerH - t * innerH,
  }));

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ minWidth: 320 }}>
        {yTicks.map((t) => (
          <g key={t.val}>
            <line x1={padL} y1={t.y} x2={chartW - padR} y2={t.y} stroke="#f1f5f9" strokeWidth="1" />
            <text x={padL - 4} y={t.y + 4} textAnchor="end" fontSize="9" fill="#94a3b8">{t.val}</text>
          </g>
        ))}

        {data.map((d, i) => {
          const groupX = padL + i * barGroupW + (barGroupW - barW) / 2;
          const totalH = ((d.completed + d.pending + d.delivered) / maxVal) * innerH;
          const compH = (d.completed / maxVal) * innerH;
          const pendH = (d.pending / maxVal) * innerH;
          const delH = (d.delivered / maxVal) * innerH;
          const baseY = padT + innerH;

          return (
            <g key={d.month}>
              {/* Delivered (bottom) */}
              <rect x={groupX} y={baseY - delH} width={barW} height={delH} rx="0" fill="#fcd34d" opacity="0.7" />
              {/* Pending (middle) */}
              <rect x={groupX} y={baseY - delH - pendH} width={barW} height={pendH} rx="0" fill="#fbbf24" opacity="0.85" />
              {/* Completed (top) */}
              <rect
                x={groupX}
                y={baseY - totalH}
                width={barW}
                height={compH}
                rx="3"
                fill="#f59e0b"
                opacity="0.9"
              />
              {/* Round top corners only on top segment */}
              <text
                x={groupX + barW / 2}
                y={padT + innerH + 14}
                textAnchor="middle"
                fontSize="9"
                fill="#94a3b8"
              >
                {d.month}
              </text>
              {/* Total label */}
              {totalH > 0 && (
                <text
                  x={groupX + barW / 2}
                  y={baseY - totalH - 4}
                  textAnchor="middle"
                  fontSize="8"
                  fill="#64748b"
                  fontWeight="600"
                >
                  {d.completed + d.pending + d.delivered}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
